import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Settings,
  Bell,
  Users,
  Mail,
  Plus,
  X,
  Check,
  Save,
  ArrowLeft,
  FileStack,
  LogOut,
  Trash2,
  AlertCircle,
  Megaphone,
  ExternalLink,
  Eye,
  EyeOff,
  Link as LinkIcon,
  Share2,
  Coins,
  Lock,
  Loader2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;
const API = `${BACKEND_URL}/api`;

const AdminSettingsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Notification settings state
  const [staffUsers, setStaffUsers] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [customEmail, setCustomEmail] = useState("");
  const [customEmailError, setCustomEmailError] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Banner settings state
  const [bannerSettings, setBannerSettings] = useState({
    enabled: false,
    message: "",
    button_text: "",
    button_url: ""
  });
  const [bannerHasChanges, setBannerHasChanges] = useState(false);
  const [bannerSaveSuccess, setBannerSaveSuccess] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);

  // Login notification settings state
  const [loginNotification, setLoginNotification] = useState({
    enabled: false,
    message: "",
    type: "info"
  });
  const [loginNotifHasChanges, setLoginNotifHasChanges] = useState(false);
  const [loginNotifSaveSuccess, setLoginNotifSaveSuccess] = useState(false);
  const [savingLoginNotif, setSavingLoginNotif] = useState(false);

  // Invite settings state
  const [inviteSettings, setInviteSettings] = useState({
    enabled: false,
    bonus_credits: 0
  });
  const [inviteHasChanges, setInviteHasChanges] = useState(false);
  const [inviteSaveSuccess, setInviteSaveSuccess] = useState(false);
  const [savingInvite, setSavingInvite] = useState(false);

  // Follower visibility settings state
  const [followerVisibility, setFollowerVisibility] = useState({
    show_followers: true
  });
  const [followerHasChanges, setFollowerHasChanges] = useState(false);
  const [followerSaveSuccess, setFollowerSaveSuccess] = useState(false);
  const [savingFollower, setSavingFollower] = useState(false);

  // Change Password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) {
      navigate("/login");
      return;
    }
    
    const userData = JSON.parse(storedUser);
    if (userData.role !== "admin") {
      navigate("/admin");
      return;
    }
    
    setUser(userData);
    fetchNotificationSettings();
    fetchBannerSettings();
    fetchLoginNotificationSettings();
    fetchInviteSettings();
    fetchFollowerVisibilitySettings();
  }, [navigate]);

  const fetchBannerSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/banner`);
      setBannerSettings({
        enabled: response.data.enabled || false,
        message: response.data.message || "",
        button_text: response.data.button_text || "",
        button_url: response.data.button_url || ""
      });
    } catch (err) {
      console.error("Failed to fetch banner settings:", err);
    }
  };

  const fetchLoginNotificationSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/login-notification`);
      setLoginNotification({
        enabled: response.data.enabled || false,
        message: response.data.message || "",
        type: response.data.type || "info"
      });
    } catch (err) {
      console.error("Failed to fetch login notification settings:", err);
    }
  };

  const fetchInviteSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/invite`);
      setInviteSettings({
        enabled: response.data.enabled || false,
        bonus_credits: response.data.bonus_credits || 0
      });
    } catch (err) {
      console.error("Failed to fetch invite settings:", err);
    }
  };

  const fetchFollowerVisibilitySettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/follower-visibility`);
      setFollowerVisibility({
        show_followers: response.data.show_followers !== false
      });
    } catch (err) {
      console.error("Failed to fetch follower visibility settings:", err);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      
      const response = await axios.get(`${API}/admin/settings/notifications`, {
        headers: {}
      });
      
      setStaffUsers(response.data.staff_users || []);
      setRecipients(response.data.application_recipients || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      console.error("Logout failed:", err.message);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const toggleStaffRecipient = (staffUser) => {
    const isSelected = recipients.some(r => r.email === staffUser.email);
    
    if (isSelected) {
      // Don't allow removing if it's the last recipient
      if (recipients.length <= 1) {
        alert("At least one notification recipient is required");
        return;
      }
      setRecipients(prev => prev.filter(r => r.email !== staffUser.email));
    } else {
      setRecipients(prev => [...prev, {
        email: staffUser.email,
        name: staffUser.name,
        type: "staff",
        staff_id: staffUser.id
      }]);
    }
    setHasChanges(true);
    setSaveSuccess(false);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const addCustomEmail = () => {
    const email = customEmail.trim().toLowerCase();
    
    if (!email) {
      setCustomEmailError("Please enter an email address");
      return;
    }
    
    if (!validateEmail(email)) {
      setCustomEmailError("Please enter a valid email address");
      return;
    }
    
    // Check if already exists
    if (recipients.some(r => r.email.toLowerCase() === email)) {
      setCustomEmailError("This email is already in the list");
      return;
    }
    
    setRecipients(prev => [...prev, {
      email: email,
      type: "custom"
    }]);
    setCustomEmail("");
    setCustomEmailError("");
    setHasChanges(true);
    setSaveSuccess(false);
  };

  const removeRecipient = (email) => {
    // Don't allow removing if it's the last recipient
    if (recipients.length <= 1) {
      alert("At least one notification recipient is required");
      return;
    }
    
    setRecipients(prev => prev.filter(r => r.email !== email));
    setHasChanges(true);
    setSaveSuccess(false);
  };

  const saveSettings = async () => {
    if (recipients.length === 0) {
      alert("At least one notification recipient is required");
      return;
    }
    
    setSaving(true);
    try {
      
      await axios.put(
        `${API}/admin/settings/notifications`,
        { application_recipients: recipients },
        { headers: {} }
      );
      
      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Banner settings handlers
  const updateBannerField = (field, value) => {
    setBannerSettings(prev => ({ ...prev, [field]: value }));
    setBannerHasChanges(true);
    setBannerSaveSuccess(false);
  };

  const saveBannerSettings = async () => {
    setSavingBanner(true);
    try {
      const response = await axios.patch(`${API}/admin/settings/banner`, bannerSettings);
      setBannerSettings(response.data.banner);
      setBannerHasChanges(false);
      setBannerSaveSuccess(true);
      setTimeout(() => setBannerSaveSuccess(false), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save banner settings");
    } finally {
      setSavingBanner(false);
    }
  };

  // Login notification handlers
  const updateLoginNotifField = (field, value) => {
    setLoginNotification(prev => ({ ...prev, [field]: value }));
    setLoginNotifHasChanges(true);
    setLoginNotifSaveSuccess(false);
  };

  const saveLoginNotification = async () => {
    setSavingLoginNotif(true);
    try {
      const response = await axios.patch(`${API}/admin/settings/login-notification`, loginNotification);
      setLoginNotification(response.data.notification);
      setLoginNotifHasChanges(false);
      setLoginNotifSaveSuccess(true);
      setTimeout(() => setLoginNotifSaveSuccess(false), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save login notification");
    } finally {
      setSavingLoginNotif(false);
    }
  };

  // Invite settings handlers
  const updateInviteField = (field, value) => {
    setInviteSettings(prev => ({ ...prev, [field]: value }));
    setInviteHasChanges(true);
    setInviteSaveSuccess(false);
  };

  const saveInviteSettings = async () => {
    setSavingInvite(true);
    try {
      const response = await axios.patch(`${API}/admin/settings/invite`, inviteSettings);
      setInviteSettings(response.data.invite);
      setInviteHasChanges(false);
      setInviteSaveSuccess(true);
      setTimeout(() => setInviteSaveSuccess(false), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save invite settings");
    } finally {
      setSavingInvite(false);
    }
  };

  // Follower visibility handlers
  const saveFollowerVisibility = async () => {
    setSavingFollower(true);
    try {
      const response = await axios.patch(`${API}/admin/settings/follower-visibility`, followerVisibility);
      setFollowerVisibility(response.data.settings);
      setFollowerHasChanges(false);
      setFollowerSaveSuccess(true);
      setTimeout(() => setFollowerSaveSuccess(false), 3000);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save follower visibility settings");
    } finally {
      setSavingFollower(false);
    }
  };

  // Handle change password for admin
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess(false);
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      setPasswordError("New password must be at least 8 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      setPasswordSuccess(true);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setTimeout(() => {
        setPasswordSuccess(false);
        setShowChangePassword(false);
      }, 2000);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00A8E8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="admin-settings">
      {/* Header */}
      <header className="bg-gray-800/50 border-b border-gray-700/50 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <h1 className="text-2xl font-bold" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.02em' }}>
                <span className="text-white">HotPeek</span>
              </h1>
              <nav className="flex items-center gap-1">
                <Link
                  to="/admin"
                  className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                  data-testid="nav-applications"
                >
                  <FileStack size={18} />
                  <span className="hidden sm:inline">Applications</span>
                </Link>
                <Link
                  to="/admin/users"
                  className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
                  data-testid="nav-users"
                >
                  <Users size={18} />
                  <span className="hidden sm:inline">Users</span>
                </Link>
                <Link
                  to="/admin/settings"
                  className="flex items-center gap-2 px-3 py-2 text-white bg-gray-700/50 rounded-lg transition-colors"
                  data-testid="nav-settings"
                >
                  <Settings size={18} />
                  <span className="hidden sm:inline">Settings</span>
                </Link>
              </nav>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-white font-medium">{user?.name}</p>
                <p className="text-gray-500 text-sm">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Settings size={28} className="text-[#00A8E8]" />
            Settings
          </h2>
          <p className="text-gray-400 mt-2">Configure application notifications and preferences</p>
        </div>

        {/* Notification Settings Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden"
        >
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-700/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Bell size={20} className="text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Application Notifications</h3>
                <p className="text-sm text-gray-400">Choose who receives email notifications for new creator applications</p>
              </div>
            </div>
            {hasChanges && (
              <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                Unsaved changes
              </span>
            )}
          </div>

          {/* Card Content */}
          <div className="p-6 space-y-6">
            {/* Staff Members Section */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Select from Team Members
              </label>
              <div className="space-y-2">
                {staffUsers.map((staff) => {
                  const isSelected = recipients.some(r => r.email === staff.email);
                  return (
                    <button
                      key={staff.id || staff.email}
                      onClick={() => toggleStaffRecipient(staff)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${
                        isSelected
                          ? "bg-[#00A8E8]/10 border-[#00A8E8]/50 text-white"
                          : "bg-gray-700/30 border-gray-600/50 text-gray-300 hover:border-gray-500"
                      }`}
                      data-testid={`staff-toggle-${staff.email}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isSelected ? "bg-[#00A8E8]/20" : "bg-gray-600"
                        }`}>
                          <Users size={18} className={isSelected ? "text-[#00A8E8]" : "text-gray-400"} />
                        </div>
                        <div className="text-left">
                          <p className="font-medium">{staff.name}</p>
                          <p className="text-sm text-gray-400">{staff.email}</p>
                        </div>
                        <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                          staff.role === "admin" 
                            ? "bg-purple-500/20 text-purple-400" 
                            : "bg-blue-500/20 text-blue-400"
                        }`}>
                          {staff.role}
                        </span>
                      </div>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        isSelected ? "bg-[#00A8E8]" : "bg-gray-600"
                      }`}>
                        {isSelected && <Check size={14} className="text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-gray-700" />
              <span className="text-gray-500 text-sm">or</span>
              <div className="flex-1 h-px bg-gray-700" />
            </div>

            {/* Custom Email Section */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Add Custom Email Address
              </label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input
                    type="email"
                    value={customEmail}
                    onChange={(e) => {
                      setCustomEmail(e.target.value);
                      setCustomEmailError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addCustomEmail();
                      }
                    }}
                    placeholder="external@example.com"
                    className={`w-full pl-10 pr-4 py-3 bg-gray-700/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all ${
                      customEmailError 
                        ? "border-red-500/50 focus:ring-red-500/50" 
                        : "border-gray-600 focus:ring-[#00A8E8]/50 focus:border-[#00A8E8]"
                    }`}
                    data-testid="custom-email-input"
                  />
                </div>
                <button
                  onClick={addCustomEmail}
                  className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center gap-2"
                  data-testid="add-email-btn"
                >
                  <Plus size={18} />
                  Add
                </button>
              </div>
              {customEmailError && (
                <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
                  <AlertCircle size={14} />
                  {customEmailError}
                </p>
              )}
            </div>

            {/* Current Recipients List */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Current Notification Recipients ({recipients.length})
              </label>
              <div className="space-y-2">
                <AnimatePresence>
                  {recipients.map((recipient) => (
                    <motion.div
                      key={recipient.email}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="flex items-center justify-between px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                          {recipient.type === "staff" ? (
                            <Users size={14} className="text-green-400" />
                          ) : (
                            <Mail size={14} className="text-green-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-white text-sm">
                            {recipient.name || recipient.email}
                          </p>
                          {recipient.name && (
                            <p className="text-gray-400 text-xs">{recipient.email}</p>
                          )}
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          recipient.type === "staff"
                            ? "bg-blue-500/20 text-blue-400"
                            : "bg-gray-500/20 text-gray-400"
                        }`}>
                          {recipient.type === "staff" ? "Team" : "Custom"}
                        </span>
                      </div>
                      <button
                        onClick={() => removeRecipient(recipient.email)}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Remove recipient"
                        data-testid={`remove-recipient-${recipient.email}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </motion.div>
                  ))}
                </AnimatePresence>
                
                {recipients.length === 0 && (
                  <div className="text-center py-8 bg-gray-700/30 rounded-lg">
                    <AlertCircle size={32} className="mx-auto text-amber-400 mb-2" />
                    <p className="text-amber-400 font-medium">No recipients selected</p>
                    <p className="text-gray-500 text-sm">At least one recipient is required</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Card Footer */}
          <div className="px-6 py-4 border-t border-gray-700/50 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {recipients.length === 1 
                ? "1 person will receive notifications" 
                : `${recipients.length} people will receive notifications`}
            </p>
            <div className="flex items-center gap-3">
              {saveSuccess && (
                <motion.span
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-1 text-green-400 text-sm"
                >
                  <Check size={16} />
                  Saved successfully
                </motion.span>
              )}
              <button
                onClick={saveSettings}
                disabled={saving || !hasChanges || recipients.length === 0}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#00A8E8] to-[#D000FF] text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-settings-btn"
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Save size={18} />
                )}
                Save Changes
              </button>
            </div>
          </div>
        </motion.div>

        {/* Info Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl"
        >
          <div className="flex gap-3">
            <Bell size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-blue-400 font-medium">How it works</p>
              <p className="text-gray-400 mt-1">
                When a new creator submits an application through the website, an email notification 
                will be sent to all selected recipients. Each email includes the applicant's details 
                and a direct link to view the full application in the admin dashboard.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Top Banner Settings Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden"
        >
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-700/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Megaphone size={20} className="text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Announcement Banner</h3>
                <p className="text-sm text-gray-400">Display a promotional message at the top of the homepage</p>
              </div>
            </div>
            {bannerHasChanges && (
              <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                Unsaved changes
              </span>
            )}
          </div>

          {/* Card Content */}
          <div className="p-6 space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-700/30 rounded-lg border border-gray-600">
              <div className="flex items-center gap-3">
                {bannerSettings.enabled ? (
                  <Eye size={20} className="text-green-400" />
                ) : (
                  <EyeOff size={20} className="text-gray-400" />
                )}
                <div>
                  <label className="block text-sm font-medium text-white">Banner Visibility</label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {bannerSettings.enabled ? "Banner is visible on the homepage" : "Banner is hidden from visitors"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => updateBannerField("enabled", !bannerSettings.enabled)}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  bannerSettings.enabled ? 'bg-green-500' : 'bg-gray-600'
                }`}
                data-testid="banner-enabled-toggle"
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${
                    bannerSettings.enabled ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Message Input */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Banner Message
              </label>
              <input
                type="text"
                value={bannerSettings.message}
                onChange={(e) => updateBannerField("message", e.target.value)}
                placeholder="e.g., 🔥 Limited Time Offer! Join now and get 20% more earnings!"
                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all"
                data-testid="banner-message-input"
              />
              <p className="text-xs text-gray-500 mt-1">This message will be displayed in the banner</p>
            </div>

            {/* Button Text & URL */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Button Text
                </label>
                <input
                  type="text"
                  value={bannerSettings.button_text}
                  onChange={(e) => updateBannerField("button_text", e.target.value)}
                  placeholder="e.g., Learn More"
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all"
                  data-testid="banner-button-text-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                  <LinkIcon size={14} />
                  Button URL
                </label>
                <input
                  type="text"
                  value={bannerSettings.button_url}
                  onChange={(e) => updateBannerField("button_url", e.target.value)}
                  placeholder="e.g., /register or https://example.com"
                  className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all"
                  data-testid="banner-button-url-input"
                />
              </div>
            </div>

            {/* Preview */}
            {(bannerSettings.message || bannerSettings.button_text) && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Preview
                </label>
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-4 text-sm">
                  <span>{bannerSettings.message || "Your message here..."}</span>
                  {bannerSettings.button_text && (
                    <button className="px-4 py-1.5 bg-white text-purple-600 rounded-full font-medium text-xs hover:bg-gray-100 transition-colors flex items-center gap-1">
                      {bannerSettings.button_text}
                      <ExternalLink size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Card Footer */}
          <div className="px-6 py-4 border-t border-gray-700/50 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {bannerSettings.enabled 
                ? "Banner is currently visible to all visitors" 
                : "Banner is currently hidden"}
            </p>
            <div className="flex items-center gap-3">
              {bannerSaveSuccess && (
                <motion.span
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-1 text-green-400 text-sm"
                >
                  <Check size={16} />
                  Saved successfully
                </motion.span>
              )}
              <button
                onClick={saveBannerSettings}
                disabled={savingBanner || !bannerHasChanges}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-banner-btn"
              >
                {savingBanner ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Save size={18} />
                )}
                Save Banner
              </button>
            </div>
          </div>
        </motion.div>

        {/* Login Notification Settings Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden"
        >
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-700/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500/20 rounded-lg">
                <AlertCircle size={20} className="text-cyan-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Login Page Notification</h3>
                <p className="text-sm text-gray-400">Display a message on the login page for users</p>
              </div>
            </div>
            {loginNotifHasChanges && (
              <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                Unsaved changes
              </span>
            )}
          </div>

          {/* Card Content */}
          <div className="p-6 space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-700/30 rounded-lg border border-gray-600">
              <div className="flex items-center gap-3">
                {loginNotification.enabled ? (
                  <Eye size={20} className="text-green-400" />
                ) : (
                  <EyeOff size={20} className="text-gray-400" />
                )}
                <div>
                  <label className="block text-sm font-medium text-white">Notification Visibility</label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {loginNotification.enabled ? "Notification is visible on login page" : "Notification is hidden"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => updateLoginNotifField("enabled", !loginNotification.enabled)}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  loginNotification.enabled ? 'bg-green-500' : 'bg-gray-600'
                }`}
                data-testid="login-notif-enabled-toggle"
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${
                    loginNotification.enabled ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Notification Type */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Notification Type
              </label>
              <div className="flex gap-3">
                {[
                  { value: "info", label: "Info", color: "cyan", icon: "ℹ️" },
                  { value: "warning", label: "Warning", color: "amber", icon: "⚠️" },
                  { value: "success", label: "Success", color: "green", icon: "✓" }
                ].map((type) => (
                  <button
                    key={type.value}
                    onClick={() => updateLoginNotifField("type", type.value)}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
                      loginNotification.type === type.value
                        ? type.value === "info" 
                          ? "bg-cyan-500/20 border-cyan-500/50 text-cyan-400"
                          : type.value === "warning"
                            ? "bg-amber-500/20 border-amber-500/50 text-amber-400"
                            : "bg-green-500/20 border-green-500/50 text-green-400"
                        : "bg-gray-700/30 border-gray-600 text-gray-400 hover:border-gray-500"
                    }`}
                    data-testid={`login-notif-type-${type.value}`}
                  >
                    <span>{type.icon}</span>
                    <span className="font-medium">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Message Input */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Notification Message
              </label>
              <textarea
                value={loginNotification.message}
                onChange={(e) => updateLoginNotifField("message", e.target.value)}
                placeholder="e.g., We're experiencing temporary issues with login. Please try again later."
                rows={3}
                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all resize-none"
                data-testid="login-notif-message-input"
              />
              <p className="text-xs text-gray-500 mt-1">This message will be displayed above the login form</p>
            </div>

            {/* Preview */}
            {loginNotification.message && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Preview
                </label>
                <div className={`px-4 py-3 rounded-lg flex items-start gap-3 ${
                  loginNotification.type === "info" 
                    ? "bg-cyan-500/10 border border-cyan-500/30 text-cyan-400"
                    : loginNotification.type === "warning"
                      ? "bg-amber-500/10 border border-amber-500/30 text-amber-400"
                      : "bg-green-500/10 border border-green-500/30 text-green-400"
                }`}>
                  <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm">{loginNotification.message}</p>
                </div>
              </div>
            )}
          </div>

          {/* Card Footer */}
          <div className="px-6 py-4 border-t border-gray-700/50 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {loginNotification.enabled 
                ? "Notification is currently visible on login page" 
                : "Notification is currently hidden"}
            </p>
            <div className="flex items-center gap-3">
              {loginNotifSaveSuccess && (
                <motion.span
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-1 text-green-400 text-sm"
                >
                  <Check size={16} />
                  Saved successfully
                </motion.span>
              )}
              <button
                onClick={saveLoginNotification}
                disabled={savingLoginNotif || !loginNotifHasChanges}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-login-notif-btn"
              >
                {savingLoginNotif ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Save size={18} />
                )}
                Save Notification
              </button>
            </div>
          </div>
        </motion.div>

        {/* Invite/Referral Settings Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-8 bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden"
        >
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-700/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Share2 size={20} className="text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Invite/Referral System</h3>
                <p className="text-sm text-gray-400">Allow users to invite friends and earn bonus credits</p>
              </div>
            </div>
            {inviteHasChanges && (
              <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                Unsaved changes
              </span>
            )}
          </div>

          {/* Card Content */}
          <div className="p-6 space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-700/30 rounded-lg border border-gray-600">
              <div className="flex items-center gap-3">
                {inviteSettings.enabled ? (
                  <Eye size={20} className="text-green-400" />
                ) : (
                  <EyeOff size={20} className="text-gray-400" />
                )}
                <div>
                  <label className="block text-sm font-medium text-white">Invite System</label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {inviteSettings.enabled ? "Users can share invite links" : "Invite functionality is disabled"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => updateInviteField("enabled", !inviteSettings.enabled)}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  inviteSettings.enabled ? 'bg-green-500' : 'bg-gray-600'
                }`}
                data-testid="invite-enabled-toggle"
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${
                    inviteSettings.enabled ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Bonus Credits */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Bonus Credits for Inviter
              </label>
              <p className="text-xs text-gray-500 mb-3">
                When an invited user purchases a credit package, the inviter will receive this bonus
              </p>
              <div className="relative">
                <Coins className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="number"
                  value={inviteSettings.bonus_credits}
                  onChange={(e) => updateInviteField("bonus_credits", Math.max(0, Math.min(10000, parseInt(e.target.value) || 0)))}
                  placeholder="0"
                  min="0"
                  max="10000"
                  className="w-full px-4 py-3 pl-10 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500 transition-all"
                  data-testid="invite-bonus-credits-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                  credits
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Set to 0 to disable bonus rewards</p>
            </div>

            {/* How it works */}
            <div className="p-4 bg-gray-700/20 rounded-lg border border-gray-700">
              <h4 className="text-sm font-medium text-white mb-2">How it works:</h4>
              <ul className="text-xs text-gray-400 space-y-1">
                <li>• Each user gets a unique invite code they can share</li>
                <li>• New users can register using an invite link</li>
                <li>• The system tracks who invited whom</li>
                <li>• When invited user buys credits, admin can give inviter a bonus</li>
              </ul>
            </div>
          </div>

          {/* Card Footer */}
          <div className="px-6 py-4 border-t border-gray-700/50 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {inviteSettings.enabled 
                ? `Invite system is active with ${inviteSettings.bonus_credits} bonus credits` 
                : "Invite system is currently disabled"}
            </p>
            <div className="flex items-center gap-3">
              {inviteSaveSuccess && (
                <motion.span
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-1 text-green-400 text-sm"
                >
                  <Check size={16} />
                  Saved successfully
                </motion.span>
              )}
              <button
                onClick={saveInviteSettings}
                disabled={savingInvite || !inviteHasChanges}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-invite-btn"
              >
                {savingInvite ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Save size={18} />
                )}
                Save Settings
              </button>
            </div>
          </div>
        </motion.div>

        {/* Follower Visibility Settings Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden"
        >
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-700/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Users size={20} className="text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Follower Count Visibility</h3>
                <p className="text-sm text-gray-400">Control follower/following count display on profiles</p>
              </div>
            </div>
            {followerHasChanges && (
              <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full">
                Unsaved changes
              </span>
            )}
          </div>

          {/* Card Content */}
          <div className="p-6 space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-700/30 rounded-lg border border-gray-600">
              <div className="flex items-center gap-3">
                {followerVisibility.show_followers ? (
                  <Eye size={20} className="text-blue-400" />
                ) : (
                  <EyeOff size={20} className="text-gray-400" />
                )}
                <div>
                  <label className="block text-sm font-medium text-white">Show Follower Counts</label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {followerVisibility.show_followers 
                      ? "Follower and following counts are visible on user profiles" 
                      : "Follower and following counts are hidden from profiles"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFollowerVisibility(prev => ({ ...prev, show_followers: !prev.show_followers }));
                  setFollowerHasChanges(true);
                  setFollowerSaveSuccess(false);
                }}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  followerVisibility.show_followers ? 'bg-blue-500' : 'bg-gray-600'
                }`}
                data-testid="follower-visibility-toggle"
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${
                    followerVisibility.show_followers ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Info */}
            <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <p className="text-sm text-blue-300">
                When disabled, users will not see follower/following counts on any profile page.
                This applies to both fan and creator profiles.
              </p>
            </div>
          </div>

          {/* Card Footer */}
          <div className="px-6 py-4 border-t border-gray-700/50 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {followerVisibility.show_followers 
                ? "Follower counts are currently visible" 
                : "Follower counts are currently hidden"}
            </p>
            <div className="flex items-center gap-3">
              {followerSaveSuccess && (
                <motion.span
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-1 text-green-400 text-sm"
                >
                  <Check size={16} />
                  Saved successfully
                </motion.span>
              )}
              <button
                onClick={saveFollowerVisibility}
                disabled={savingFollower || !followerHasChanges}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-follower-visibility-btn"
              >
                {savingFollower ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Save size={18} />
                )}
                Save Settings
              </button>
            </div>
          </div>
        </motion.div>

        {/* Change Password Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden"
          data-testid="change-password-card"
        >
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-gray-700/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Lock className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Password & Security</h3>
                <p className="text-sm text-gray-500">Update your admin password</p>
              </div>
            </div>
            {!showChangePassword && (
              <button
                onClick={() => setShowChangePassword(true)}
                className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors text-sm font-medium"
                data-testid="change-password-btn"
              >
                Change Password
              </button>
            )}
          </div>

          {/* Card Content */}
          {showChangePassword && (
            <div className="px-6 py-6">
              <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                {passwordError && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                    <AlertCircle size={16} />
                    {passwordError}
                  </div>
                )}
                {passwordSuccess && (
                  <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2 text-green-400 text-sm">
                    <Check size={16} />
                    Password changed successfully!
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Current Password</label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all"
                    placeholder="Enter current password"
                    required
                    data-testid="admin-current-password"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all"
                    placeholder="Enter new password (min. 8 characters)"
                    required
                    minLength={8}
                    data-testid="admin-new-password"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all"
                    placeholder="Confirm new password"
                    required
                    minLength={8}
                    data-testid="admin-confirm-password"
                  />
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowChangePassword(false);
                      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
                      setPasswordError("");
                    }}
                    className="flex-1 px-4 py-3 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={changingPassword}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:opacity-90 transition-opacity font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="admin-submit-password-btn"
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      "Update Password"
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default AdminSettingsPage;
